import React, { createContext, useContext, useReducer, ReactNode } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { AlertColor } from "@mui/material";

type NotificationPosition = {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
};

export type NotificationOptions = {
    duration?: number;
    position?: NotificationPosition;
};

export type Notification = {
    id: number;
    type: AlertColor;
    title?: ReactNode;
    message: ReactNode;
    options?: NotificationOptions;
};

type NotificationState = {
    notifications: Notification[];
};

type NotificationAction =
    | { type: "ADD_NOTIFICATION"; notification: Omit<Notification, "id"> }
    | { type: "REMOVE_NOTIFICATION"; id: number };

const defaultState: NotificationState = {
    notifications: [],
};

const NotificationContext = createContext<{
    state: NotificationState;
    dispatch: React.Dispatch<NotificationAction>;
    showNotification: (type: AlertColor, message: ReactNode, title?: ReactNode, options?: NotificationOptions) => void;
} | null>(null);

let nextId = 1;

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
    switch (action.type) {
        case "ADD_NOTIFICATION":
            return {
                ...state,
                notifications: [...state.notifications, { ...action.notification, id: nextId++ }],
            };
        case "REMOVE_NOTIFICATION":
            return {
                ...state,
                notifications: state.notifications.filter((notification) => notification.id !== action.id),
            };
        default:
            return state;
    }
}

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(notificationReducer, defaultState);

    const showNotification = (type: AlertColor, message: ReactNode, title?: ReactNode, options?: NotificationOptions) => {
        dispatch({
            type: "ADD_NOTIFICATION",
            notification: { type, message, title, options },
        });
    };

    return (
        <NotificationContext.Provider value={{ state, dispatch, showNotification }}>
            {children}
            <NotificationDisplay />
        </NotificationContext.Provider>
    );
};

const NotificationDisplay: React.FC = () => {
    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }

    const { state, dispatch } = context;

    const handleClose = (id: number) => {
        dispatch({ type: "REMOVE_NOTIFICATION", id });
    };

    return (
        <>
            {state.notifications.map((notification) => (
                <Snackbar
                    key={notification.id}
                    open={true}
                    autoHideDuration={notification.options?.duration || 3000}
                    onClose={() => handleClose(notification.id)}
                    anchorOrigin={notification.options?.position || { vertical: "top", horizontal: "right" }}
                >
                    <Alert onClose={() => handleClose(notification.id)} severity={notification.type} variant="filled">
                        {notification.title && <strong>{notification.title}</strong>}
                        {notification.title ? ": " : ""}
                        {notification.message}
                    </Alert>
                </Snackbar>
            ))}
        </>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }

    return {
        showSuccess: (message: ReactNode, title?: ReactNode, options?: NotificationOptions) => {
            context.showNotification("success", message, title, options);
        },
        showError: (message: ReactNode, title?: ReactNode, options?: NotificationOptions) => {
            context.showNotification("error", message, title, options);
        },
        showWarning: (message: ReactNode, title?: ReactNode, options?: NotificationOptions) => {
            context.showNotification("warning", message, title, options);
        },
        showInfo: (message: ReactNode, title?: ReactNode, options?: NotificationOptions) => {
            context.showNotification("info", message, title, options);
        },
    };
};
